import type { $Fetch } from 'ofetch';
import type {
  AgendamentoRecursoGateway, ListAgendamentoRecursosParams,
} from '@/application/port/out/AgendamentoRecursoGateway';
import type { AgendamentoRecurso, AgendamentoRecursoCommand } from '@/domain/model/Agendamento';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/**
 * HTTP gateway pro CRUD admin de AgendamentoRecurso.
 * Authority: acadadmin:agendamento_recurso:read|manage.
 *
 * @since G.5.9
 */
export class HttpAgendamentoRecursoGateway implements AgendamentoRecursoGateway {
  constructor(private readonly client: $Fetch) {}

  async list(params: ListAgendamentoRecursosParams): Promise<Page<AgendamentoRecurso>> {
    const query: Record<string, string | number> = {
      page: params.page ?? 0,
      size: params.size ?? 50,
    };
    if (params.tipo) query.tipo = params.tipo;
    const res = await this.client<ApiResponse<Page<AgendamentoRecurso>>>(
      '/api/v1/admin/agendamento-recursos', { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }

  async get(id: string): Promise<AgendamentoRecurso> {
    const res = await this.client<ApiResponse<AgendamentoRecurso>>(
      `/api/v1/admin/agendamento-recursos/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async create(cmd: AgendamentoRecursoCommand): Promise<AgendamentoRecurso> {
    const res = await this.client<ApiResponse<AgendamentoRecurso>>(
      '/api/v1/admin/agendamento-recursos', { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async update(id: string, cmd: AgendamentoRecursoCommand): Promise<AgendamentoRecurso> {
    const res = await this.client<ApiResponse<AgendamentoRecurso>>(
      `/api/v1/admin/agendamento-recursos/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async deactivate(id: string): Promise<void> {
    await this.client(
      `/api/v1/admin/agendamento-recursos/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}
